import ErrorIcon from 'ui/elements/icons/ErrorIcon';
import React, { useState } from 'react';
import styles from './styles.scss';
import FileIcon from 'domain/shared/Files/File/FileIcon';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import { CSSProperties } from 'react';
import Tooltip from 'ui/elements/Tooltip';
import cx from 'classnames';
import Loading from 'ui/elements/Loading';
import { UploadStatus } from '../Upload/useFileUpload';
import ImagePreview from './ImagePreview';
import { isImageFile } from '../utils';
import IconButton from 'ui/elements/icons/IconButton';
import { bluePlanetTheme } from 'ui/theme';
import { alpha } from '@mui/material';

interface Props {
  name: string;
  status: UploadStatus;
  onClose: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  style?: CSSProperties;
  onDownload?: () => void;
  file?: File;
}

export default function UploadedFile(props: Props) {
  const { name, status } = props;
  const [isDownloading, setIsDownloading] = useState(false);

  const onClick = props.onDownload
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        if (props.onDownload) {
          setIsDownloading(true);
          props.onDownload();
          setIsDownloading(false);
        }
      }
    : undefined;

  // Check if this is an image file
  const isImage = isImageFile(name);

  // For images, render ImagePreview with status overlays
  if (isImage && props.file) {
    return (
      <div className={cx(props.className)} style={props.style} onClick={props.onDownload ? onClick : undefined}>
        <div style={{ position: 'relative' }}>
          <ImagePreview
            localFile={props.file}
            filename={name}
            maxWidth={60}
            maxHeight={47}
            style={{ width: '60px', height: '47px' }}
          />

          {/* Status overlays */}
          <div
            style={{
              position: 'absolute',
              top: '1px',
              right: '1px',
              zIndex: 10,
            }}
          >
            {status === 'UPLOADING' ? (
              <Loading size={16} color="white" />
            ) : status === 'UPLOAD_ERROR' ? (
              <ErrorIcon color="red" />
            ) : !isDownloading ? (
              <IconButton
                size="small"
                onClick={props.onClose}
                color="white"
                hoverColor="white"
                sx={{
                  backgroundColor: alpha(bluePlanetTheme.bluePlanetPalette.black, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(bluePlanetTheme.bluePlanetPalette.black, 0.8),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // For non-images, use the original layout
  return (
    <div
      className={cx(styles.container, styles.border, styles.whiteBackground, props.className, {
        [styles.downloadable]: !!props.onDownload,
      })}
      style={props.style}
      onClick={props.onDownload ? onClick : undefined}
    >
      <Tooltip title={name}>
        <div className={styles.nameAndIcon}>
          {status === 'UPLOAD_ERROR' ? (
            <ErrorIcon className="u-half-spacing-right" color="red" />
          ) : isDownloading ? (
            <Loading className="u-half-spacing-right" style={{ height: '24px' }} size={24} />
          ) : (
            <FileIcon className="u-half-spacing-right" name={name} />
          )}
          {name}
        </div>
      </Tooltip>
      <div className={styles.status}>
        {status === 'UPLOADING' ? (
          <Loading style={{ verticalAlign: 'middle', padding: '2px' }} size={20} />
        ) : !isDownloading ? (
          <IconButton color="indigo" size="small" onClick={props.onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
